import React, { useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import NotesIcon from "@mui/icons-material/Notes";
import KeyIcon from "@mui/icons-material/Key";
import LockIcon from "@mui/icons-material/Lock";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ReleaseNotesCRUD from "./ReleaseNotes";
import AuthTokensCRUD from "./AuthTokens";
import BucketAccessListsCRUD from "./BucketAccessLists";

const TABS = [
  {
    key: "release-notes",
    label: "Release Notes",
    icon: <NotesIcon fontSize="small" />,
    description: "Manage release notes for your buckets",
    Component: ReleaseNotesCRUD,
  },
  {
    key: "bucket-access-lists",
    label: "Bucket Access Lists",
    icon: <KeyIcon fontSize="small" />,
    description: "Control access permissions for storage buckets",
    Component: BucketAccessListsCRUD,
  },
  {
    key: "auth-tokens",
    label: "Auth Tokens",
    icon: <LockIcon fontSize="small" />,
    description: "Create, rotate, and revoke authentication tokens",
    Component: AuthTokensCRUD,
  },
];

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function AdminHome({ user }) {
  const isAdmin = !!user?.is_qli_admin

  const getInitialTab = () => {
    const stored = localStorage.getItem("admin-home-tab");
    const idx = TABS.findIndex((t) => t.key === stored);
    return idx >= 0 ? idx : 0;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    document.title = "QLI Artifacts | Admin";
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    localStorage.setItem("admin-home-tab", TABS[newValue].key);
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* Admin Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3f51b5 100%)",
          color: "white",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <AdminPanelSettingsIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                Admin Console
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<SettingsIcon sx={{ color: "white !important" }} />}
              label={isAdmin ? "Admin Access" : "Read-Only"}
              sx={{
                backgroundColor: isAdmin ? "rgba(76, 175, 80, 0.3)" : "rgba(255, 255, 255, 0.2)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            />
            {user?.username && (
              <Chip
                label={user.username}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  color: "white",
                  fontWeight: 500,
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              backgroundColor: "#3f51b5",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              minHeight: 56,
              px: 3,
              color: "#616161",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                color: "#3f51b5",
              },
              "&.Mui-selected": {
                color: "#3f51b5",
                backgroundColor: "#f0f3ff",
              },
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      {TABS.map((tab, idx) => {
        const Component = tab.Component;
        return (
          <TabPanel key={tab.key} value={activeTab} index={idx}>
            <Component admin={isAdmin} />
          </TabPanel>
        );
      })}
    </Box>
  );
}